import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Container,
  Divider,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { REPORT_TYPES } from 'src/utils/constants';
import ReportFilters from 'src/components/reports/ReportFilters';
import FilterSelectInput from 'src/common/FilterSelectInput';
import { getReportConfig } from 'src/components/reports/reportService';
import ScreenHeader from 'src/common/ScreenHeader';
import OtherReportsTable from 'src/components/otherReports/OtherReportsTable';
import { resetOtherReportData } from 'src/redux/reportSlice';
import { useDispatch, useSelector } from 'react-redux';
import FilterIcon from '@mui/icons-material/FilterAltOutlined';

const OtherReports = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { otherReportList } = useSelector((state) => state.reports);
  const { appSettings } = useSelector((state) => state.settings);

  // Responsive breakpoints
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // < 600px

  const [reportName, setReportName] = useState(REPORT_TYPES[0].id);
  const [reportConfig, setReportConfig] = useState(null);
  const [tableLimit, setTableLimit] = useState({ pageNo: 1, limit: 10 });

  useEffect(() => {
    const config = getReportConfig(reportName);
    setReportConfig(config);
  }, [reportName]);

  useEffect(
    () => () => {
      dispatch(resetOtherReportData());
    },
    []
  );

  const getReportTypes = () => {
    let OTHER_REPORT_TYPES = REPORT_TYPES;
    if (!appSettings?.vehicle_management) {
      OTHER_REPORT_TYPES = REPORT_TYPES.filter((report) => report.vms === false);
    }
    return OTHER_REPORT_TYPES;
  };

  const handleGroupChange = (value) => {
    setReportName(value);
    if (otherReportList.length > 0) {
      dispatch(resetOtherReportData());
    }
  };

  const handleLimitChange = (limit) => {
    setTableLimit(limit);
  };

  return (
    <>
      <ScreenHeader title="Other Reports" />

      <Box
        sx={{
          background: 'linear-gradient(135deg, #f9f9fc, #eef1f6)',
          minHeight: '100vh',
          py: { xs: 2, sm: 3, md: 4 },
          px: { xs: 1, sm: 2 },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            px: { xs: 1, sm: 2, md: 3 },
          }}
        >
          <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
            {/* Report Selection Card */}
            <Grid item xs={12}>
              <Card
                sx={{
                  position: 'relative',
                  borderRadius: { xs: 3, sm: 4 },
                  overflow: 'hidden',
                  p: 0,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  background: 'rgba(255, 255, 255, 0.85)',
                  backdropFilter: 'blur(6px)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    padding: '1.5px',
                    borderRadius: 'inherit',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main})`,
                    WebkitMask:
                      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    pointerEvents: 'none',
                    opacity: 0.9,
                  },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 6px 30px rgba(0,0,0,0.12)',
                    transform: { xs: 'none', sm: 'translateY(-2px)' },
                  },
                }}
              >
                <Box
                  sx={{
                    px: { xs: 2, sm: 2.5, md: 3 },
                    py: { xs: 2, sm: 2, md: 2 },
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: { xs: 2, sm: 0 },
                    background:
                      'linear-gradient(90deg, rgba(33,150,243,0.1), rgba(156,39,176,0.08))',
                    borderTopLeftRadius: { xs: 12, sm: 16 },
                    borderTopRightRadius: { xs: 12, sm: 16 },
                  }}
                >
                  <FilterSelectInput
                    sx={{
                      width: { xs: '100%', sm: '50%', md: '35%' },
                      mb: 0,
                    }}
                    label="Select Report Type"
                    onInputChange={handleGroupChange}
                    data={getReportTypes()}
                    value={reportName}
                  />

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: { xs: 'center', sm: 'flex-end' },
                      gap: 1,
                      px: { xs: 2, sm: 2.2 },
                      py: { xs: 1, sm: 1.2 },
                      borderRadius: 2,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main})`,
                      boxShadow: '0 2px 8px rgba(9, 50, 91, 0.3)',
                      transition: '0.3s ease',
                      '&:hover': {
                        boxShadow: '0 3px 10px rgba(106,27,154,0.4)',
                        transform: { xs: 'none', sm: 'scale(1.03)' },
                      },
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#fff',
                        fontWeight: 500,
                        letterSpacing: 0.3,
                        fontSize: { xs: '0.875rem', sm: '0.938rem', md: '1rem' },
                        textAlign: 'center',
                      }}
                    >
                      {isMobile ? 'Reports' : 'View / Download Reports'}
                    </Typography>
                    <FilterIcon
                      sx={{
                        fontSize: { xs: 18, sm: 20 },
                        color: '#fff',
                      }}
                    />
                  </Box>
                </Box>

                <Divider />

                {reportConfig && (
                  <Box
                    sx={{
                      p: { xs: 2, sm: 2.5, md: 3 },
                    }}
                  >
                    <ReportFilters
                      title={reportName}
                      datePickerProps={reportConfig.dateConfig}
                      filterConfig={reportConfig.filterOptions}
                      formModel={reportConfig.formConfig?.formModel}
                      formValidation={reportConfig.formConfig?.validation}
                      formInitValues={reportConfig.formConfig?.initialValues}
                      downloadUrl={reportConfig.downloadUrl}
                      tableLimits={tableLimit}
                    />
                  </Box>
                )}
              </Card>
            </Grid>

            {/* Report Table */}
            <Grid item xs={12}>
              <Card
                sx={{
                  position: 'relative',
                  borderRadius: { xs: 3, sm: 4 },
                  overflow: 'hidden',
                  p: { xs: 2, sm: 2.5, md: 3 },
                  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                  background: 'rgba(255,255,255,0.9)',
                  backdropFilter: 'blur(8px)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    padding: '1.5px',
                    borderRadius: 'inherit',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main})`,
                    WebkitMask:
                      'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    pointerEvents: 'none',
                    opacity: 0.9,
                  },
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 6px 30px rgba(0,0,0,0.12)',
                    transform: { xs: 'none', sm: 'translateY(-2px)' },
                  },
                }}
              >
                <OtherReportsTable
                  onLimitChange={handleLimitChange}
                  reportName={reportName}
                />
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default OtherReports;
